import { useRef, useEffect } from "react";

/**
 * Singleton installer for the global Shift-key listener that toggles the
 * `shift-key-held` class on <body>. While the class is present, CSS in
 * `index.css` disables text selection across the whole page, which prevents
 * the browser from highlighting row text during shift-click range selections.
 *
 * Installed lazily the first time any component uses `useRangeSelection` and
 * reference-counted so the listeners are removed when no consumers remain.
 */
let shiftListenerRefCount = 0;
let shiftListenersInstalled = false;
let shiftKeyDownHandler: ((e: KeyboardEvent) => void) | null = null;
let shiftKeyUpHandler: ((e: KeyboardEvent) => void) | null = null;
let shiftBlurHandler: (() => void) | null = null;

function installShiftKeyListeners() {
  if (shiftListenersInstalled || typeof window === "undefined") return;
  shiftListenersInstalled = true;

  shiftKeyDownHandler = (e: KeyboardEvent) => {
    if (e.key === "Shift") {
      document.body.classList.add("shift-key-held");
    }
  };
  shiftKeyUpHandler = (e: KeyboardEvent) => {
    if (e.key === "Shift") {
      document.body.classList.remove("shift-key-held");
    }
  };
  shiftBlurHandler = () => {
    document.body.classList.remove("shift-key-held");
  };

  window.addEventListener("keydown", shiftKeyDownHandler);
  window.addEventListener("keyup", shiftKeyUpHandler);
  window.addEventListener("blur", shiftBlurHandler);
}

function uninstallShiftKeyListeners() {
  if (!shiftListenersInstalled || typeof window === "undefined") return;
  shiftListenersInstalled = false;
  if (shiftKeyDownHandler) window.removeEventListener("keydown", shiftKeyDownHandler);
  if (shiftKeyUpHandler) window.removeEventListener("keyup", shiftKeyUpHandler);
  if (shiftBlurHandler) window.removeEventListener("blur", shiftBlurHandler);
  shiftKeyDownHandler = null;
  shiftKeyUpHandler = null;
  shiftBlurHandler = null;
  document.body.classList.remove("shift-key-held");
}

/**
 * Adds shift-click range-select behaviour to any checkbox list.
 *
 * Usage
 * -----
 *   const { handleItemClick } = useRangeSelection(
 *     orderedIds,          // visible, ordered list of IDs (string | number)
 *     selectedIds,         // current selection as a Set
 *     onSelectionChange,   // callback that receives the full next Set
 *     listIdentityKey,     // optional – a string that changes when the list's
 *                          //   identity changes (sort, filter, page switch, etc.)
 *                          //   The anchor is reset whenever this key changes.
 *   );
 *
 *   // In JSX – capture shiftKey on the wrapper element, *not* in onCheckedChange:
 *   <div onClick={(e) => { e.stopPropagation(); handleItemClick(id, e.shiftKey); }}>
 *     <Checkbox checked={selected.has(id)} onCheckedChange={() => {}} />
 *   </div>
 *
 * Behaviour
 * ---------
 * - Plain click  : toggles the item; anchors here for the next shift-click.
 * - Shift-click  : selects (or deselects) every item from the last anchor to
 *                  the clicked item, inclusive. The range state mirrors the
 *                  new state of the shift-clicked row.
 * - Anchor reset : the anchor is cleared whenever (a) it falls outside
 *                  orderedIds (filtered/paginated away) OR (b) listIdentityKey
 *                  changes (sort, filter, page switch, etc.). Either way the
 *                  next click behaves as a plain click.
 */
export function useRangeSelection<T extends string | number>(
  orderedIds: T[],
  selectedIds: Set<T>,
  onSelectionChange: (next: Set<T>) => void,
  listIdentityKey?: string,
) {
  const anchorRef = useRef<T | null>(null);
  const prevIdentityKeyRef = useRef<string | undefined>(listIdentityKey);

  useEffect(() => {
    if (prevIdentityKeyRef.current !== listIdentityKey) {
      anchorRef.current = null;
      prevIdentityKeyRef.current = listIdentityKey;
    }
  }, [listIdentityKey]);

  useEffect(() => {
    shiftListenerRefCount += 1;
    installShiftKeyListeners();
    return () => {
      shiftListenerRefCount -= 1;
      if (shiftListenerRefCount <= 0) {
        shiftListenerRefCount = 0;
        uninstallShiftKeyListeners();
      }
    };
  }, []);

  function handleItemClick(id: T, shiftKey: boolean) {
    const anchorIdx =
      anchorRef.current !== null ? orderedIds.indexOf(anchorRef.current as T) : -1;

    if (!shiftKey || anchorIdx === -1) {
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      anchorRef.current = id;
      onSelectionChange(next);
      return;
    }

    const clickedIdx = orderedIds.indexOf(id);
    if (clickedIdx === -1) {
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      anchorRef.current = id;
      onSelectionChange(next);
      return;
    }

    const lo = Math.min(anchorIdx, clickedIdx);
    const hi = Math.max(anchorIdx, clickedIdx);
    const newState = !selectedIds.has(id);
    const next = new Set(selectedIds);
    for (let i = lo; i <= hi; i++) {
      if (newState) next.add(orderedIds[i]);
      else next.delete(orderedIds[i]);
    }
    onSelectionChange(next);
  }

  return { handleItemClick };
}
